import { useEffect } from 'react';

import type { CalendarAppointmentEvent } from 'components/mrp/components/Calendar/Calendar.types';
import type { CalendarPosition } from '../CalendarNavigationHandler.types';

type UseCalendarCleanupParams = {
  calendars: Record<string, unknown>;
  position: CalendarPosition | undefined;
  calendarEvents: { [calendarId: string]: CalendarAppointmentEvent[] };
  resetPosition: () => void;
  setEvent: (event?: CalendarAppointmentEvent) => void;
  removeCalendarEventsForCalendarIds: (calendarIds: string[]) => void;
};

export const useCalendarCleanup = ({
  calendars,
  position,
  calendarEvents,
  resetPosition,
  setEvent,
  removeCalendarEventsForCalendarIds
}: UseCalendarCleanupParams) => {
  'use memo';

  useEffect(() => {
    // If the calendar that owns the current position was closed, reset.
    if (position && !calendars[position.calendarId]) {
      resetPosition();
      setEvent(undefined);
    }

    // Dispose of events for calendars that are no longer open.
    const openedCalendarIds = Object.keys(calendars);
    const closedCalendarIds = Object.keys(calendarEvents).filter(
      (id) => !openedCalendarIds.includes(id)
    );

    removeCalendarEventsForCalendarIds(closedCalendarIds);

    // Only `calendars`, `setEvent`, and `removeCalendarEventsForCalendarIds` are listed as
    // deps. `position`, `calendarEvents`, and `resetPosition` are intentionally omitted:
    // - Including `position` would re-run the effect on every navigation step, which is wrong.
    //   The effect's job is to react to calendar config changes, not position changes.
    // - Including `calendarEvents` would cause an infinite loop: the effect calls
    //   `removeCalendarEventsForCalendarIds` which updates `calendarEvents`, re-triggering the effect.
    // - `resetPosition` and `calendarEvents` are read via refs (updated in useLayoutEffect) so
    //   the effect always sees their latest values without needing them as reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendars, setEvent, removeCalendarEventsForCalendarIds]);
};
